import React, { useEffect, useState } from 'react';
import { Button, Caption, LinkText, Paragraph, Stack } from '@mediahuis/chameleon-react';
import { useBrandGroupConfig } from '../../hooks/useBrandGroupConfig';

import { Trans, useTranslation } from 'react-i18next';
import './accountConsent.css';
import { useQueryParams } from '../../hooks/useQueryParams';
import {
  EventActionValues,
  EventCategoryValues,
  EventLabelValues,
  VIEWS,
} from '../../types/tracking';
import { useTracking } from '../../hooks/useTracking';
import { setContext } from '../../redux/reducers/contextReducer';
import { useAppDispatch } from '../../redux/hooks';
import { useAuthenticationContext } from '../../providers/AuthenticationProvider';
import { authContinue } from '../../utils/authUtils';
import { buildConsentSwitchUrl } from '../../utils/switchAccount';

function AccountConsent() {
  const { t } = useTranslation();
  const brandGroupConfigHook = useBrandGroupConfig();
  const { brand, consentId, initiateLoginUri } = useQueryParams();
  const brandConfig = brandGroupConfigHook.getBrandConfigFromBrandId(brand);
  const { user } = useAuthenticationContext();
  const dispatch = useAppDispatch();
  const { trackEvent, trackEventWithCategory } = useTracking();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitConsent = async () => {
    if (!consentId) return;
    trackEvent(EventActionValues.Click, EventLabelValues.ContinueAsKnownUser);

    authContinue();
  };

  const useDifferentAccount = () => {
    const url = buildConsentSwitchUrl({ initiateLoginUri });
    if (!url) return;
    trackEvent(EventActionValues.Click, EventLabelValues.ContinueAsOtherUser);
    window.location.href = url;
  };

  // Offered only when there is a restart target, i.e. when buildConsentSwitchUrl can build a URL.
  const canSwitchAccount = !!initiateLoginUri;

  useEffect(() => {
    // On page load and brand/brandGroup present
    dispatch(
      setContext({
        eventCategory: EventCategoryValues.Login,
        view: VIEWS.ACCOUNT_CONSENT,
      })
    );
    if (brand)
      trackEventWithCategory(
        EventActionValues.Show,
        EventLabelValues.ContinueAsKnownUser,
        EventCategoryValues.Login
      );
    // Only depending on brand because otherwise the useEffect would be triggered multiple times
    // and incomplete events would be tracked
    // Also get the brand from query params instead of registrationFlowConfig for the same reason
  }, [brand]);

  return (
    <Stack alignItems="center" flexDirection="column" className="account-consent px-6 m-auto">
      <Paragraph size="Paragraph" className="account-consent__text--dark text-center mb-6 mt-7">
        <Trans
          i18nKey={'accountConsent.consent.emailText'}
          components={{ afterDisclaimer: <br /> }}
          values={{ email: user?.email }}></Trans>
      </Paragraph>
      <Caption size="lg" className="account-consent__text--light text-center">
        <Trans
          i18nKey="accountConsent.consent.terms"
          components={{
            termsLink: (
              <LinkText
                target="_blank"
                href={brandConfig?.termsAndConditions.languages.nl}
                className="account-consent__text--light with-underline">
                {t('accountConsent.consent.terms.termsLink')}
              </LinkText>
            ),
            policyLink: (
              <LinkText
                target="_blank"
                href={brandConfig?.privacyStatement.languages.nl}
                className="account-consent__text--light with-underline">
                {t('accountConsent.consent.terms.policyLink')}
              </LinkText>
            ),
          }}
          values={{ urlDisplay: brandConfig?.urlDisplay }}
        />
      </Caption>
      <Button
        size="lg"
        onClick={submitConsent}
        appearance="primary"
        data-cy="consentButton"
        className="mb-3"
        disabled={isSubmitting}
        loading={isSubmitting}>
        {t('accountConsent.consent.buttonText')}
      </Button>
      {canSwitchAccount && (
        <LinkText
          size="Paragraph"
          data-cy="otherAccountButton"
          onClick={useDifferentAccount}
          className="account-consent__text--light text-center with-underline">
          {t('accountConsent.consent.buttonLinkText')}
        </LinkText>
      )}
    </Stack>
  );
}

export default AccountConsent;
